/**
 * An image field, and **the place the Wave 3 close's §6.1 is actually fixed**.
 *
 * The finding: an `image` block carries no dimensions, so a rendered page
 * shifts on mobile. An `<img>` with no `width` and `height` has no box until
 * its bytes arrive — the browser lays the page out around a zero-height
 * element and re-lays it out when the image decodes, so everything below it
 * jumps, mid-read, on the slow connection where it is worst.
 *
 * The close said *capture at upload*. **Since FR-I13 there is an upload**
 * (ADR-0021), and a picture's width and height come from wherever it came
 * from: the library measured its own on the server, and a shipped graphic's
 * are in its manifest — so the browser measures only a pasted address. Either
 * way the two numbers go on the block beside the URL
 * (`BLOCK_SCHEMAS['image']`), the renderer puts them on the tag, and the box
 * is reserved before the first byte.
 *
 * **C24 item 9: one button, not three.** Until then this field drew *Upload*,
 * *Choose from library* and a URL box side by side, and `LogoControl` drew a
 * fourth flow of its own. Now there is one **Choose a picture** button and one
 * `PicturePicker` (ADR-0047 §2) — the firm's library with upload and drop in
 * it, the graphics the product ships, and a link. The URL box stays as the
 * field's own input, because an address already set must remain visible and
 * editable in place; what went is the row of competing buttons.
 *
 * Three properties this keeps:
 *
 * 1. **Failing to measure is not failing to save.** An image the browser
 *    cannot load — CORS, a typo, a host that is down — still saves, with no
 *    dimensions, exactly as it would have before. The page shifts, which is
 *    the old behaviour and not a new one; refusing the save would be worse.
 * 2. **The numbers are never typed.** They are the image's own. A field a firm
 *    fills in by hand is a field that will disagree with the picture.
 * 3. **Re-measured when the URL changes**, because a firm that swaps a
 *    portrait for a landscape and keeps the old dimensions gets a reserved box
 *    of the wrong shape — which is a worse defect than no box at all, since it
 *    is silent and it looks deliberate.
 */

import { Button, Field, Input } from '@fiveox/ui'
import { Check, ImageOff, Images, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { PicturePicker, type PickedPicture } from '../../../components/site/PicturePicker'
import { SITE, useEvaluation } from '../../../lib/access'

export interface Dimensions {
  width: number
  height: number
}

/**
 * Measure an image in the browser. Resolves `null` for anything that does not
 * load — never rejects, because a measurement is an improvement and not a
 * precondition.
 *
 * Exported for its test: it is the one piece of this file that is testable
 * without a DOM event loop, given a fake `Image`.
 */
export function measure(
  url: string,
  makeImage: () => HTMLImageElement = () => new Image(),
): Promise<Dimensions | null> {
  return new Promise((resolve) => {
    if (!url.trim()) {
      resolve(null)
      return
    }
    const img = makeImage()
    // Ten seconds. Long enough for a large photograph on a bad connection,
    // short enough that a dead host does not leave a spinner on the screen of
    // somebody who has already moved on.
    const timer = setTimeout(() => resolve(null), 10_000)
    img.onload = () => {
      clearTimeout(timer)
      const width = img.naturalWidth
      const height = img.naturalHeight
      resolve(width > 0 && height > 0 ? { width, height } : null)
    }
    img.onerror = () => {
      clearTimeout(timer)
      resolve(null)
    }
    img.src = url
  })
}

export function ImageField({
  label,
  value,
  onChange,
  onMeasured,
  required,
  hint,
  disabled,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  /** Called with the image's own dimensions, or `null` when it could not be
   *  measured — so the caller can clear stale numbers rather than keep them. */
  onMeasured?: (dimensions: Dimensions | null) => void
  required?: boolean
  hint?: string
  disabled?: boolean
}) {
  const [state, setState] = useState<'idle' | 'measuring' | 'ok' | 'failed'>('idle')
  const [size, setSize] = useState<Dimensions | null>(null)
  const measured = useRef<string | null>(null)
  const { can } = useEvaluation()
  const canUpload = can(SITE, 'can_update') && !disabled
  const [picking, setPicking] = useState(false)

  /**
   * A picked picture, from wherever the window got it.
   *
   * **Known dimensions short-circuit the measurement**: `measured.current` is
   * set to the picked URL before `onChange`, so the effect below sees a URL it
   * has already "measured" and does not load the bytes to re-derive numbers it
   * was handed. A pasted link arrives with `null`s and is measured as before.
   */
  function use(picture: PickedPicture) {
    const known = picture.width !== null && picture.height !== null
    measured.current = known ? picture.url : null
    setSize(known ? { width: picture.width as number, height: picture.height as number } : null)
    setState(known ? 'ok' : 'idle')
    onChange(picture.url)
    if (known) latest.current?.({ width: picture.width as number, height: picture.height as number })
  }

  // **The callback is held in a ref, and that is load-bearing.** Measuring is
  // asynchronous and can take seconds; `onMeasured` closes over the block's
  // props as they were when the measurement started. Calling the stale one
  // would write those props back — silently undoing whatever the firm typed in
  // a sibling field while the image loaded. The ref is always the latest.
  const latest = useRef(onMeasured)
  latest.current = onMeasured

  useEffect(() => {
    const url = value.trim()
    if (!url || url === measured.current) return
    let cancelled = false
    setState('measuring')
    void measure(url).then((dimensions) => {
      if (cancelled) return
      measured.current = url
      setSize(dimensions)
      setState(dimensions ? 'ok' : 'failed')
      latest.current?.(dimensions)
    })
    return () => {
      cancelled = true
    }
    // Deliberately only `value`: `onMeasured` is a fresh closure on every
    // render of the block form, so depending on it would re-measure on every
    // keystroke in a sibling field. The ref above is what keeps it current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <Field label={label} hint={hint} labelAside={required ? undefined : <span className="text-muted-foreground text-xs">optional</span>}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Choose a picture, or paste https://…"
        inputMode="url"
        disabled={disabled}
      />
      <div className="mt-1.5 flex flex-wrap gap-2">
        {/* C24 item 9: one button, one window. */}
        {!disabled && (
          <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
            <Images size={13} aria-hidden /> Choose a picture
          </Button>
        )}
      </div>
      <PicturePicker
        open={picking}
        onClose={() => setPicking(false)}
        onPick={use}
        editable={canUpload}
        value={value}
      />
      <div className="mt-1.5 flex items-center gap-3">
        {value.trim() ? (
          <img
            src={value}
            alt=""
            className="h-12 w-16 rounded border border-border object-cover"
            loading="lazy"
          />
        ) : null}
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {state === 'measuring' && (
            <>
              <Loader2 size={12} className="animate-spin" /> Measuring…
            </>
          )}
          {state === 'ok' && size && (
            <>
              <Check size={12} className="text-emerald-700" /> {size.width} × {size.height}. Your page
              will keep this space while the picture loads.
            </>
          )}
          {state === 'failed' && (
            <>
              <ImageOff size={12} />
              We could not load that image to measure it. It will still be saved — but the page may
              jump slightly as it loads.
            </>
          )}
        </p>
      </div>
    </Field>
  )
}
